import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Home } from "lucide-react";
import axios from "axios";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Total poll: 12 attempts × 2.5s ≈ 30s (Stripe webhook usually <10s,
// but we keep a comfortable margin so we almost never fall back to the
// soft message).
const MAX_POLL_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2500;

const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  // `softPending` = we couldn't confirm paid within the poll window, but
  // nothing critical failed — Stripe will finish processing, an email
  // will arrive. We show a friendly message instead of a scary error.
  const [softPending, setSoftPending] = useState(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      // No session_id at all → likely direct access to /success, show soft state
      setSoftPending(true);
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= MAX_POLL_ATTEMPTS) {
      // Final fallback: treat as soft-pending rather than "Erreur"
      setSoftPending(true);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/checkout/status/${sid}`);
      const data = r?.data || {};
      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        setBookingId(data.booking_id || null);
        setLoading(false);
      } else if (data.status === "expired") {
        // Truly expired session — show soft message rather than block the user
        setSoftPending(true);
        setLoading(false);
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), POLL_INTERVAL_MS);
      }
    } catch (e) {
      // Log for debugging but never show raw error to the user
      // eslint-disable-next-line no-console
      console.error("checkout/status error:", extractErrorMessage(e));
      // Retry instead of giving up after the first network hiccup
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), POLL_INTERVAL_MS);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 font-['DM_Sans']">
      <div style={{ maxWidth: 450, width: "100%", textAlign: "center", padding: "4rem 0" }}>
        {loading ? (
          <>
            <Loader2 data-testid="loading-spinner" style={{ width: 48, height: 48, color: "#b8977e", margin: "0 auto 1.5rem", animation: "spin 1s linear infinite" }} />
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.6rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.5rem" }}>Vérification du paiement...</h2>
            <p style={{ color: "#777", fontSize: "0.9rem" }}>Cela peut prendre quelques secondes.</p>
          </>
        ) : softPending ? (
          <>
            {/* Soft fallback — payment likely went through, confirmation by email */}
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#faf8f5", border: "1px solid #e8d5c4", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem", color: "#b8977e", fontSize: "1.6rem" }} data-testid="soft-pending-icon">
              ⏳
            </div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.6rem", fontWeight: 600, color: "#b8977e", marginBottom: "0.75rem" }} data-testid="soft-pending-title">
              Paiement enregistré
            </h2>
            <p style={{ color: "#5a5a5a", fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: "0.92rem", lineHeight: 1.65, marginBottom: "2rem" }} data-testid="soft-pending-msg">
              Votre paiement est en cours de confirmation par Stripe. Vous recevrez un email avec tous les détails dans les prochaines minutes.
              <br /><br />
              <span style={{ color: "#999", fontSize: "0.82rem" }}>
                En cas de problème&nbsp;: <a href="mailto:walid.societe.pro@gmail.com" style={{ color: "#b8977e", textDecoration: "underline" }}>walid.societe.pro@gmail.com</a>
              </span>
            </p>
            <button
              data-testid="back-home-btn-soft"
              onClick={() => navigate("/")}
              style={{
                background: "linear-gradient(135deg, #b8977e, #c9a88a)", color: "#fff", border: "none", borderRadius: 50,
                padding: "0.9rem 2.5rem", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, cursor: "pointer",
                boxShadow: "0 4px 20px rgba(184,151,126,0.3)",
              }}
            >
              <Home style={{ display: "inline", width: 16, height: 16, verticalAlign: "middle", marginRight: 8 }} />
              Retour à l'accueil
            </button>
          </>
        ) : (
          <>
            {/* Animated checkmark */}
            <svg data-testid="success-icon" width="80" height="80" viewBox="0 0 80 80" style={{ margin: "0 auto 1.5rem", display: "block" }}>
              <circle cx="40" cy="40" r="36" fill="none" stroke="#b8977e" strokeWidth="3" opacity="0.2" />
              <circle cx="40" cy="40" r="36" fill="none" stroke="#b8977e" strokeWidth="3" strokeDasharray="226" strokeDashoffset="0" style={{ animation: "checkDraw 0.8s ease forwards" }} />
              <path d="M24 42 L35 53 L56 30" fill="none" stroke="#b8977e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="48" strokeDashoffset="0" style={{ animation: "checkDraw 0.8s ease 0.3s forwards" }} />
            </svg>
            <style>{`@keyframes checkDraw { from { stroke-dashoffset: 226; } to { stroke-dashoffset: 0; } }`}</style>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.8rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.5rem" }}>Réservation confirmée !</h2>
            <p style={{ color: "#555", fontSize: "0.95rem", marginBottom: "2rem", lineHeight: 1.6 }}>
              Vous allez recevoir un email de confirmation avec tous les détails de votre réservation.
            </p>
            {bookingId && (
              <p style={{ color: "#999", fontSize: "0.8rem", marginBottom: "1.5rem" }}>
                Référence : <span style={{ fontFamily: "monospace" }}>{bookingId}</span>
              </p>
            )}
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/")}
              style={{
                background: "linear-gradient(135deg, #b8977e, #c9a88a)", color: "#fff", border: "none", borderRadius: 50,
                padding: "0.9rem 2.5rem", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, cursor: "pointer",
                boxShadow: "0 4px 20px rgba(184,151,126,0.3)",
              }}
            >
              <Home style={{ display: "inline", width: 16, height: 16, verticalAlign: "middle", marginRight: 8 }} />
              Retour à l'accueil
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
